import Hls from 'hls.js';
import React, { useCallback, useEffect, useRef, useState } from 'react';

type VideoPlayerProps = {
  src: string;           // .m3u8 url
  poster?: string;
  autoPlay?: boolean;
  muted?: boolean;
  className?: string;
};

function formatTime(sec: number) {
  if (!isFinite(sec) || sec < 0) return "--:--";
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  const m = Math.floor((sec / 60) % 60).toString().padStart(2, "0");
  const h = Math.floor(sec / 3600);
  return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
}

export default function VideoPlayer({
  src,
  poster,
  autoPlay = false,
  muted = false,
  className = "",
}: VideoPlayerProps) {

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [buffered, setBuffered] = useState<number>(0); // 0–1 for UI
  const [error, setError] = useState<string | null>(null);

  // Attach HLS
  useEffect(() => {
    const video = videoRef.current!;
    // Clean up previous instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari / iOS
      video.src = src;
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        // helpful defaults; tweak if needed
        enableWorker: true,
        lowLatencyMode: true,
        backBufferLength: 60,
      });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (autoPlay) video.play().catch(() => { });
      });
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal) {
          console.error("HLS Fatal Error:", data);
          setError("⚠️ Unable to play this video (HLS error).");
          hls.destroy();
        }
      });
    } else {
      // Very old browser: last-ditch
      video.src = src;
    }

    const onError = () => {
      console.error("Video element error:", video.error);
      setError("⚠️ Video cannot be played.");
    };

    const onLoadedMetadata = () => setDuration(video.duration || 0);
    const onTimeUpdate = () => setCurrentTime(video.currentTime || 0);
    const onProgress = () => {
      try {
        const b = video.buffered;
        const t = video.currentTime;
        for (let i = 0; i < b.length; i++) {
          if (b.start(i) <= t && t <= b.end(i)) {
            const pct = duration ? b.end(i) / duration : 0;
            setBuffered(Math.max(0, Math.min(1, pct)));
            break;
          }
        }
      } catch { }
    };
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    video.addEventListener("error", onError);
    video.addEventListener("loadedmetadata", onLoadedMetadata);
    video.addEventListener("timeupdate", onTimeUpdate);
    video.addEventListener("progress", onProgress);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);

    return () => {
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("progress", onProgress);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, autoPlay, duration]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current!;
    if (v.paused || v.ended) {
      v.play().catch(() => { });
    } else {
      v.pause();
    }
  }, []);


  const onScrub = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current!;
    const value = Number(e.target.value);
    v.currentTime = (value / 1000) * (isFinite(duration) ? duration : 0);
  }, [duration]);

  const onScrubStart = useCallback(() => {
    videoRef.current?.pause();
  }, []);

  const onScrubEnd = useCallback(() => {
    if (isPlaying) {
      videoRef.current?.play().catch(() => { });
    }
  }, [isPlaying]);


  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1
    }}>


      <div style={{

      }}>
        <div style={{
          position: "relative", background: "#000", borderRadius: 12, overflow: "hidden"
        }}>
          <video
            ref={videoRef}
            poster={poster}
            playsInline
            controls={false}
            muted={muted}
            style={{
              width: '100%',
              height: '435px', display: "block"
            }}
          />
          {/* Center play button overlay (clickable) */}
          <button
            onClick={togglePlay}
            aria-label={isPlaying ? "Pause" : "Play"}
            style={{
              position: "absolute",
              inset: 0,
              margin: "auto",
              width: 64,
              height: 64,
              borderRadius: "9999px",
              border: 0,
              background: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(4px)",
              cursor: "pointer",
              display: isPlaying ? "none" : "grid",
              placeItems: "center",
            }}
          >
            ▶
          </button>
        </div>
        {error && (
          <div style={{ color: "red", fontSize: 14, marginTop: 8, fontWeight: 500 }}>
            {error}
          </div>
        )}
        {/* Controls */}
        {!error && (
          <div style={{ display: "grid", gap: 8, marginTop: 8 }}>
            {/* Progress with buffered track */}
            <div style={{ position: "relative", height: 20 }}>
              {/* Buffered bar */}
              <div
                aria-hidden
                style={{
                  position: "absolute",
                  height: 4,
                  top: 8,
                  left: 0,
                  right: 0,
                  background: "#e5e7eb",
                  borderRadius: 999,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${buffered * 100}%`,
                    height: "100%",
                    background: "#cbd5e1",
                  }}
                />
              </div>

              {/* Seek slider */}
              <input
                type="range"
                min={0}
                max={1000}
                value={
                  duration && isFinite(duration)
                    ? Math.round((currentTime / duration) * 1000)
                    : 0
                }
                onChange={onScrub}
                onMouseDown={onScrubStart}
                onMouseUp={onScrubEnd}
                onTouchStart={onScrubStart}
                onTouchEnd={onScrubEnd}

                aria-label="Seek"
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  background: "transparent",
                  WebkitAppearance: "none",
                  appearance: "none",
                  height: 20,
                }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={togglePlay}
                style={{
                  padding: "6px 12px",
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                {isPlaying ? "Pause" : "Play"}
              </button>

              <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12 }}>
                <>
                  {formatTime(currentTime)} / {formatTime(duration)}
                </>
              </div>

              {/* Mute */}
              <button
                onClick={() => {
                  const v = videoRef.current!;
                  v.muted = !v.muted;
                }}
                style={{
                  padding: "6px 12px",
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Toggle Mute
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
